import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';

import tw, { css } from 'twin.macro';

import { Spacer } from '@components/Spacer';

import { Layout } from '@auth_module/components/Layout';
import { useUser } from '@auth_module/hooks/useUser';
import { resendEmailVerification } from '@auth_module/services';
import { Loader } from '@components/Loader';
import { CircleCheckIcon } from '@icons/CircleCheckIcon';
import verifyGraphic from '@assets/images/verify-email-graphic.svg';

const Container = tw.div`w-full max-w-[440px] m-auto px-6 md:px-0`;
const CenteredContent = tw.div`h-full w-full flex items-center justify-center`;
const Title = tw.h3`text-accent-1 text-[24px] text-center leading-[140%] font-semibold`;
const StyledP = tw.p`text-center text-base font-normal text-accent-1 leading-[140%]`;
const ErrorMessage = tw(StyledP)`text-error mt-4 text-center`;
const SuccessIndicator = tw.div`bg-success-bg border border-solid border-success p-[16px] rounded-[8px] flex items-center justify-start gap-[16px] mt-[36px]`;
const IconWrapper = tw.div`rounded-full w-[44px] h-[44px] flex items-center justify-center bg-success-icon-bg`;

const backgroundImage = css`
  background-image: url(${verifyGraphic});
`;

export const VerifyPendingPage = () => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [error, setError] = useState<Error | { message: string } | null>(null);
  const { user } = useUser();

  const handleResendEmailVerification = async () => {
    setIsLoading(true);
    setIsSuccess(false);
    setError(null);

    try {
      const response = await resendEmailVerification();

      if (response && response.status === 200) {
        setIsSuccess(true);
      }
    } catch (error: unknown | Error) {
      setError(error as Error);
      setIsSuccess(false);
    } finally {
      setIsLoading(false);
    }
  };

  const errorMessage = useMemo(() => {
    if (!error) return null;

    if (error.message && error.message === 'Too Many Attempts.') {
      return 'Too many attempts. Please check your email and wait a few moments before trying again.';
    } else {
      return error?.message;
    }
  }, [error]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsSuccess(false);
    }, 3000);

    return () => clearTimeout(timeout);
  }, [isSuccess]);

  return (
    <Layout tw='md:top-[calc(50% - 128px)] md:max-w-[536px]' backgroundCss={backgroundImage}>
      <CenteredContent>
        <Container>
          <Title>{t('auth.verify_page.title')}</Title>
          <Spacer size='xxxl' direction='vertical' />
          <StyledP>
            <Trans i18nKey={'auth.verify_page.message'} values={{ email: user?.email }}>
              We sent you a verification email to <strong />. Please check your inbox to verify and
              and activate this account. If you don&apos;t see the email in your inbox, please check
              your spam folder or
              <button tw='text-accent-1 underline' onClick={handleResendEmailVerification}>
                resend another email.
              </button>
            </Trans>
          </StyledP>
          {error && <ErrorMessage>{errorMessage}</ErrorMessage>}

          {isLoading ? (
            <Loader size='md' theme='dark' />
          ) : (
            isSuccess && (
              <SuccessIndicator>
                <IconWrapper>
                  <CircleCheckIcon tw='stroke-success-text-and-icon' width='32' height='32' />
                </IconWrapper>
                <p tw='text-success-text-and-icon font-medium text-[18px]'>
                  {t('auth.verify_page.success_message')}
                </p>
              </SuccessIndicator>
            )
          )}
        </Container>
      </CenteredContent>
    </Layout>
  );
};
